import { InputNumber } from 'antd';
import DietaryTagSelector from 'components/DietaryTags/DietaryTagSelector';
import { ModalMode } from 'containers/MenuManagement/constants';
import { ModifierContext } from 'containers/MenuManagement/context';
import ModifierModal from 'containers/MenuManagement/Modifiers/ModifierModal/index';
import { orderBy } from 'lodash-es';
import React, {
  useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import DraggableTable from 'components/DraggableTable';
import { nestedModifierGroupIdFromSelection, resolveNestedModifierForDisplay } from 'utils/menu';
import { OptionMode } from '../constants';
import columns from './columns';
import OptionSelector from './OptionSelector';

function matchOptionKey(option) {
  return option?.id ?? option?.localId;
}

function OptionsTable({
  options, setOptions, flushRef,
}) {
  const { modifiers } = useContext(ModifierContext);
  const [editingOption, setEditingOption] = useState();
  const tempEditingOption = useRef({});
  const [mode, setMode] = useState(ModalMode.CLOSED);
  const onRemoveItem = (e, id) => {
    e.preventDefault();
    const newOptions = options.map((o) => {
      if (o.id ? o.id === id : o.localId === id) {
        o.type = 'DELETED';
      }
      return o;
    });
    setOptions(newOptions);
  };

  const commitPendingEdit = useCallback((sourceOptions = options) => {
    if (!editingOption) {
      return sourceOptions;
    }
    const editKey = matchOptionKey(editingOption);
    const pending = tempEditingOption.current;
    if (editKey == null || !pending?.title) {
      return sourceOptions;
    }
    return sourceOptions.map((option) => {
      if (matchOptionKey(option) !== editKey) {
        return option;
      }
      return {
        ...option,
        ...pending,
        type: option.type === OptionMode.NEW ? OptionMode.NEW : OptionMode.UPDATED,
      };
    });
  }, [editingOption, options]);

  const onSave = () => {
    const editedOptions = commitPendingEdit([...options]);
    setOptions(editedOptions);
    setEditingOption();
    tempEditingOption.current = {};
  };

  useImperativeHandle(flushRef, () => ({
    flush: () => {
      if (!editingOption) {
        return null;
      }
      const nextOptions = commitPendingEdit([...options]);
      setOptions(nextOptions);
      setEditingOption();
      tempEditingOption.current = {};
      return nextOptions;
    },
  }), [commitPendingEdit, editingOption, options, setOptions]);

  const getNestedOptions = useCallback((modifierChildren) => {
    if (typeof modifierChildren[0] !== 'number') return modifierChildren;
    const nestedOptions = [];
    modifierChildren.forEach((childId) => {
      const modifier = modifiers.find((m) => m.id === childId);
      nestedOptions.push(modifier);
    });
    return nestedOptions;
  }, [modifiers]);

  const tableColumns = useMemo(() => columns({
    editingOption,
    tempEditingOption,
    setEditingOption,
    onRemoveItem,
    onSave,
    getNestedOptions,
  }).map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'title' ? 'text' : col.dataIndex === 'dietary_tags' ? 'tag' : 'number',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: record.key === editingOption?.key,
      }),
    };
  }), [editingOption, tempEditingOption, tempEditingOption, onRemoveItem, getNestedOptions]);

  const editingNestedModifier = useMemo(
    () => (editingOption?.modifiers_children?.length
      ? resolveNestedModifierForDisplay(editingOption.modifiers_children, modifiers)
      : undefined),
    [editingOption, modifiers],
  );

  useEffect(() => {
    if (editingOption?.modifiers_children?.length && editingNestedModifier) {
      setMode(ModalMode.EDIT);
      return;
    }
    if (editingOption?.modifiers_children?.length && !editingNestedModifier) {
      setMode(ModalMode.CLOSED);
      return;
    }
    if (!editingOption?.modifiers_children?.length) {
      setMode(ModalMode.CLOSED);
    }
  }, [editingOption, editingNestedModifier]);

  const sortedOptions = useMemo(() => {
    const indexedOptions = options?.map((option) => ({
      ...option,
      key: (option.id || option.localId),
    })).filter((o) => o.type !== OptionMode.DELETED) || [];
    return orderBy(indexedOptions, ['position'], ['asc']);
  }, [options]);

  const reorderPositions = (items) => {
    const reorderedOptions = items.map((item, index) => ({
      ...item,
      position: index + 1,
      type: item.position === index + 1 ? item.type : OptionMode.UPDATED,
    }));
    setOptions(reorderedOptions);
  };

  const onChangeOption = (key, value) => {
    tempEditingOption.current = { ...tempEditingOption.current, [key]: value };
  };

  const closeNestedModifierModal = () => {
    setMode(ModalMode.CLOSED);
    setEditingOption();
    tempEditingOption.current = {};
  };

  return (
    <>
      <DraggableTable
        columns={tableColumns}
        dataSource={sortedOptions}
        setDataSource={reorderPositions}
        showHeader={false}
        scroll={false}
        CustomCell={(props) => <EditableCell {...props} onChange={onChangeOption} modifiers={modifiers} />}
      />
      {editingOption && editingOption.modifiers_children?.length > 0 && editingNestedModifier && (
        <ModifierModal
          mode={mode}
          setMode={setMode}
          modifier={editingNestedModifier}
          onNestedClose={closeNestedModifierModal}
        />
      )}
    </>
  );
}

function EditableCell({
  editing,
  inputType,
  record,
  index,
  children,
  onCancel,
  onSave,
  onChange,
  dataIndex,
  title,
  modifiers,
  ...restProps
}) {
  if (editing && !record.modifiers_children?.length) {
    return (
      <td {...restProps}>
        {' '}
        {inputType === 'text' ?
          <OptionSelector
            option={record.title}
            setOption={(selected) => {
              onChange('title', selected.value);
              const groupId = nestedModifierGroupIdFromSelection(selected, modifiers);
              if (groupId != null) {
                onChange('modifiers_children', [groupId]);
              }
            }}
          /> :
          inputType === 'tag' ?
            <DietaryTagSelector defaultValue={record.dietary_tags} onChange={(value) => onChange('dietary_tags', value)} maxTagCount={1} /> : (
              <InputNumber
                value={record.real_price}
                type="number"
                placeholder="Price"
                controls={false}
                prefix="$"
                style={{ width: '100%' }}
                onChange={(real_price) => onChange('real_price', real_price)}
              />
            )}
      </td>
    );
  }
  return (
    <td {...restProps}>
      {children}
    </td>
  );
}

export default OptionsTable;
